import React from 'react';
import moment from 'moment';
import { env } from 'utils/utils';
import { Prescription } from '../../../types/types';
import Modal from '../Modal';

import('@photonhealth/elements').catch(console.error);

type ModalProps = {
  open: boolean;
  patientPhotonId: string;
  photonRedirectTo: string;
  onCloseModal: () => void;
  onAddPrescriptions: (prescriptions: Prescription[]) => void;
};

function PrescriptionForm({
  open,
  patientPhotonId,
  photonRedirectTo,
  onCloseModal,
  onAddPrescriptions,
}: ModalProps) {
  const photonPrescribeRef = React.createRef();

  React.useEffect(() => {
    const current = photonPrescribeRef.current as HTMLElement;

    if (!current) return () => {};

    const handlePrescriptionsCreated = (e: any) => {
      const orderCreatedAt = moment().toISOString();

      const prescriptions = e.detail.prescriptions.map((prescription: any) => {
        const adaptedPrescription: Prescription = {
          id: prescription.id,
          prescriptionPhotonId: prescription.id,
          medicationName: prescription.treatment.name,
          dose: '', // In Photon this value comes from the name (e.g. 'amoxicillin [250 MG] Oral Capsule')
          numberToBeTaken: '', // In Photon this value comes from the patient instructions, which may or may not include other unrelated information
          frequency: '', // In Photon this value comes from patient instructions, which may or may not include other unrelated information
          numberDispensed: prescription.dispenseQuantity,
          numberOfRefills: prescription.fillsAllowed - 1,
          doNotSubstitute: prescription.dispenseAsWritten,
          dispenseUnit: prescription.dispenseUnit,
          daysSupply: prescription.daysSupply,
          instructions: prescription.instructions,
          createdAt: orderCreatedAt,
        };

        return adaptedPrescription;
      });

      onAddPrescriptions(prescriptions);
    };

    current.addEventListener('photon-prescriptions-created', handlePrescriptionsCreated);

    return () => {
      current.removeEventListener('photon-prescriptions-created', handlePrescriptionsCreated);
    };
  }, [photonPrescribeRef, onAddPrescriptions]);

  return (
    <photon-client
      id={env('RIVER_PHOTON_CLIENT_ID')}
      org={env('RIVER_PHOTON_ORG_ID')}
      dev-mode={env('RIVER_ENV') !== 'production'}
      auto-login="true"
      redirect-uri={photonRedirectTo.trim()}
    >
      {open ? (
        <Modal onClose={onCloseModal} show={open}>
          <div className="p-4 tablet:min-w-[740px]">
            <photon-prescribe-workflow
              ref={photonPrescribeRef}
              patient-id={patientPhotonId}
              enable-order="true"
              enable-send-to-patient="true"
              enable-local-pickup="false"
              enable-delivery-pharmacies="false"
            />
          </div>
        </Modal>
      ) : null}
    </photon-client>
  );
}

export default PrescriptionForm;
